import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import apis from '../../../modules/apis'
import { useLessonInfo, useLessonScript } from '../../../modules/apis/student/lesson/review/queries'
import { axiosPost } from '../../../modules/axiosHelper'
import { useCountdownTimer } from '../../../modules/hooks/common/useCountdownTimer'
import useIsAdminLogin from '../../../modules/hooks/common/useIsAdminLogin'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import { s_lesson_review } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { track } from '../../../modules/track'
import { IS_PARENT, LOCATION, LOG_CATEGORY, LOG_TYPE, STUDY_REVIEW } from '../../../modules/vars'
import { tracker } from '../../review/useTracking'
import AudioPlayer from '../materials/AudioPlayer'
import RecordedScript from './RecordedScript'

interface RecordedClassReviewProps {
  currentUser: any
  lessonId: number
}

const RecordedClassReview = ({ currentUser, lessonId }: RecordedClassReviewProps) => {
  const [audioCurrentTime, setAudioCurrentTime] = useState(0)
  const [isAudioAuto, setIsAudioAuto] = useState(false)
  const [lastDuration, setLastDuration] = useState(null)
  const [audioLogId, setAudioLogId] = useState(null)

  const isMobile = useIsMobile()
  const { t } = useTranslation()

  const { data: lessonInfo } = useLessonInfo(lessonId)
  const { data: script } = useLessonScript(lessonId)

  const [mp3Checked, setMp3Checked] = useState(
    lessonInfo?.review_study?.list.find((item, i) => item.type == STUDY_REVIEW.MP3)?.value || false
  )

  useEffect(() => {
    tracker('review')
    return () => tracker('review', true)
  })

  const { start, pause, isRunning } = useCountdownTimer({
    timer: 10000,
    onExpire: () => {
      checkMp3()
    },
  })

  const handleEndAudio = async () => {
    toast(t(s_lesson_review.completeMp3))
    checkMp3()
    setAudioLogId(null)
    setLastDuration(null)
  }

  const checkMp3 = async () => {
    if (!mp3Checked && !IS_PARENT(currentUser)) {
      const payload = {
        action_type: STUDY_REVIEW.MP3,
      }
      const response = await axiosPost(apis.lessons.review.study(lessonId), payload)
      if (response) {
        setMp3Checked(true)
      }
    }
  }
  const handlePlayAudio = () => {
    start()
  }
  const handlePauseAudio = () => {
    pause()
    setAudioLogId(null)
    setLastDuration(null)
  }

  const audioRef = useRef<HTMLAudioElement>(null)

  const [showAudioBar, setShowAudioBar] = useState(
    lessonInfo?.allow_record && lessonInfo?.script_permission && script?.url
  )
  const isAdminLogin = useIsAdminLogin()

  useEffect(() => {
    setShowAudioBar(lessonInfo?.allow_record && lessonInfo?.script_permission && script?.url)
  }, [lessonInfo, script])

  // 수업 녹음 듣기
  useEffect(() => {
    if (isRunning) {
      let duration = lastDuration || 0
      let log_id = audioLogId
      let playback_time = audioCurrentTime || 0
      duration += 1
      playback_time += 1
      if (duration >= 5 && !isAdminLogin) {
        track({
          log_type: LOG_TYPE.LESSON.LISTEN,
          log_category: LOG_CATEGORY.LISTEN,
          location: LOCATION.REVIEW,
          lesson_id: lessonId,
          duration: duration,
          log_id: log_id,
          playback_time: playback_time,
        }).then((res) => {
          if (!log_id) {
            log_id = res?.log?.id
            setAudioLogId(log_id)
          }
          if (res?.log?.duration > 10) {
            checkMp3()
          }
        })
      }
      setLastDuration(duration)
    }
  }, [isRunning, audioCurrentTime])

  if (!lessonInfo) return null
  // if (!script || !lessonInfo) return null

  return (
    <div className="h-full">
      <div className="mx-auto flex h-full justify-center overflow-hidden">
        <div className="sm:border-r-1 relative h-full w-full border-0 border-gray-200 sm:w-[576px] lg:w-[688px] xl:w-[688px]">
          <RecordedScript
            script={script}
            lesson={lessonInfo}
            audioCurrentTime={audioCurrentTime}
            setAudioCurrentTime={setAudioCurrentTime}
            setIsAudioAuto={setIsAudioAuto}
          />
          {showAudioBar && (
            <div className="fixed left-0 bottom-0 mx-auto flex w-screen flex-row justify-center  sm:bottom-[20px]">
              <div className="border-1 min-h-0 w-full rounded-[8px] border-gray-200 sm:flex sm:min-h-[68px] sm:w-[576px] lg:w-[688px] xl:w-[688px]">
                <AudioPlayer
                  onPlay={() => handlePlayAudio()}
                  onPause={() => handlePauseAudio()}
                  onEnd={handleEndAudio}
                  onClickRange={(time) => console.log(time)}
                  ref={audioRef}
                  src={script?.url}
                  playbacktime={0}
                  isMobile={isMobile}
                  audioCurrentTime={audioCurrentTime}
                  setAudioCurrentTime={setAudioCurrentTime}
                  isAudioAuto={isAudioAuto}
                  setIsAudioAuto={setIsAudioAuto}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RecordedClassReview
